/**
 * LocalizationService - Facade for localization functionality
 *
 * Per R-4: Facade pattern - delegates to focused service classes
 * Provides multi-language support with lazy loading and fallback chains
 *
 * @module services
 */
import { LocaleLoader } from './localization/LocaleLoader.js';
import { LocaleRegistry } from './localization/LocaleRegistry.js';
import { TranslationProvider } from './localization/TranslationProvider.js';
// ============================================================================
// Pure Utility Functions (Extracted for testability)
// ============================================================================
/**
 * List of supported locale codes
 */
export const SUPPORTED_LOCALES = [
    'en',
    'ja',
    'de',
    'fr',
    'ko',
    'zh',
];
/**
 * Extract 2-letter locale code from longer locale strings
 * Pure function - no side effects, easy to test
 *
 * @param locale - Locale string (e.g., 'en-US', 'ja', 'de-DE')
 * @returns 2-letter locale code or null
 *
 * @example
 * ```typescript
 * extractLocaleCode('en-US') // 'en'
 * extractLocaleCode('ja') // 'ja'
 * extractLocaleCode('zh-CN') // 'zh' (the prefix is supported)
 * extractLocaleCode('pt-BR') // null (not supported)
 * ```
 */
export function extractLocaleCode(locale) {
    const code = locale.split('-')[0].toLowerCase();
    return SUPPORTED_LOCALES.includes(code) ? code : null;
}
/**
 * Resolve locale from preference chain
 * Pure function - determines locale based on priority without side effects
 *
 * Priority: explicit > guild > system > fallback
 *
 * @param preference - Locale preference with fallback chain
 * @returns Resolved locale code
 *
 * @example
 * ```typescript
 * resolveLocaleFromPreference({
 *     explicit: 'de',
 *     guild: 'ja',
 *     system: 'en-US',
 *     fallback: 'en'
 * }); // Returns 'de' (highest priority)
 *
 * resolveLocaleFromPreference({
 *     explicit: null,
 *     guild: 'ja-JP',
 *     system: 'en',
 *     fallback: 'en'
 * }); // Returns 'ja' (extracted from guild)
 * ```
 */
export function resolveLocaleFromPreference(preference) {
    // 1. Try explicit user selection (highest priority)
    if (preference.explicit && SUPPORTED_LOCALES.includes(preference.explicit)) {
        return preference.explicit;
    }
    // 2. Try guild/server preference
    if (preference.guild) {
        const guildLocale = extractLocaleCode(preference.guild);
        if (guildLocale && SUPPORTED_LOCALES.includes(guildLocale)) {
            return guildLocale;
        }
    }
    // 3. Try user's system language
    if (preference.system) {
        const systemLocale = extractLocaleCode(preference.system);
        if (systemLocale && SUPPORTED_LOCALES.includes(systemLocale)) {
            return systemLocale;
        }
    }
    // 4. Fallback
    return preference.fallback;
}
/**
 * LocalizationService - Main entry point for localization features
 *
 * Refactored for testability: Supports dependency injection of loader, registry, and translator
 *
 * @example Basic usage (static API)
 * ```typescript
 * import { LocalizationService } from 'xivdyetools-core';
 *
 * // Set locale (lazy loads locale data)
 * await LocalizationService.setLocale('ja');
 *
 * // Get localized label
 * const label = LocalizationService.getLabel('dye'); // "カララント:"
 *
 * // Get localized dye name
 * const dyeName = LocalizationService.getDyeName(5729); // "スノウホワイト"
 * ```
 *
 * @example Instance-based usage for testing
 * ```typescript
 * const mockLoader = new MockLocaleLoader();
 * const service = new LocalizationService({ loader: mockLoader });
 * await service.setLocale('ja');
 * ```
 *
 * @example Discord bot locale resolution
 * ```typescript
 * const preference: LocalePreference = {
 *     explicit: interaction.options.getString('language'),
 *     guild: interaction.guildLocale,
 *     system: interaction.locale,
 *     fallback: 'en'
 * };
 * await LocalizationService.setLocaleFromPreference(preference);
 * ```
 */
export class LocalizationService {
    // Instance dependencies (injectable for testing)
    loader;
    registry;
    translator;
    currentLocale = 'en';
    isInitialized = false;
    // Default singleton instance for static API compatibility
    // Per Issue #6: Eager initialization to avoid race conditions in concurrent scenarios
    static defaultInstance = new LocalizationService();
    /**
     * Constructor with optional dependency injection
     * @param config Configuration with optional loader, registry, and translator
     */
    constructor(config = {}) {
        this.loader = config.loader || new LocaleLoader();
        this.registry = config.registry || new LocaleRegistry();
        this.translator = config.translator || new TranslationProvider(this.registry);
    }
    /**
     * Get the default singleton instance
     * Per Issue #6: Returns eagerly-initialized instance to avoid race conditions
     */
    static getDefault() {
        return this.defaultInstance;
    }
    /**
     * Set active locale (loads locale file if not cached)
     *
     * ⚠️ CONCURRENCY (BUG-006, 2026-07-18 audit): `currentLocale` is shared
     * mutable state on this instance (and on the process-wide singleton for the
     * static API). In concurrent multi-locale servers (Cloudflare Workers,
     * Node servers), a per-request `setLocale` races with other requests —
     * request A can read another request's locale after any `await`. Such
     * servers must NOT call `setLocale` per request; instead call
     * `ensureLocaleLoaded(locale)` once and pass the request's locale
     * explicitly to every getter (`getDyeName(id, locale)`), or use the
     * stateless `LocaleLoader`/`LocaleRegistry`/`TranslationProvider` trio.
     *
     * @param locale - Locale code ('en', 'ja', 'de', 'fr', 'ko', 'zh')
     * @throws {AppError} If locale file fails to load
     *
     * @example
     * ```typescript
     * await service.setLocale('ja');
     * console.log(service.getCurrentLocale()); // 'ja'
     * ```
     */
    async setLocale(locale) {
        // Check if already loaded
        if (this.registry.hasLocale(locale)) {
            this.currentLocale = locale;
            this.isInitialized = true;
            return;
        }
        // Load locale data (synchronous as of v1.1.3, async signature kept for API compatibility)
        await Promise.resolve(); // Satisfy async requirement while maintaining sync behavior
        const localeData = this.loader.loadLocale(locale);
        this.registry.registerLocale(localeData);
        this.currentLocale = locale;
        this.isInitialized = true;
    }
    /**
     * Static method: Set locale using default instance
     */
    static async setLocale(locale) {
        return this.getDefault().setLocale(locale);
    }
    /**
     * Ensure a locale's data is loaded/registered WITHOUT mutating the current
     * locale. BUG-006 (2026-07-18 audit): the race-safe alternative to a
     * per-request `setLocale` — combine with explicit-locale getters
     * (`getDyeName(id, locale)`).
     */
    async ensureLocaleLoaded(locale) {
        if (this.registry.hasLocale(locale)) {
            return;
        }
        // Load locale data (synchronous as of v1.1.3, async signature kept for API compatibility)
        await Promise.resolve();
        this.registry.registerLocale(this.loader.loadLocale(locale));
    }
    /**
     * Static method: Ensure a locale is loaded in the default instance
     */
    static async ensureLocaleLoaded(locale) {
        return this.getDefault().ensureLocaleLoaded(locale);
    }
    /**
     * Set locale from preference object with priority chain
     * Uses pure function resolveLocaleFromPreference for resolution logic
     *
     * Priority: explicit > guild > system > fallback
     *
     * @param preference - Locale preference with fallback chain
     *
     * @example
     * ```typescript
     * // Discord bot usage
     * await service.setLocaleFromPreference({
     *     explicit: 'de',     // User selected German
     *     guild: 'ja',        // Server is Japanese
     *     system: 'en-US',    // User's system is English
     *     fallback: 'en'
     * });
     * // Sets locale to 'de' (highest priority)
     * ```
     */
    async setLocaleFromPreference(preference) {
        const locale = resolveLocaleFromPreference(preference);
        await this.setLocale(locale);
    }
    /**
     * Static method: Set locale from preference using default instance
     */
    static async setLocaleFromPreference(preference) {
        return this.getDefault().setLocaleFromPreference(preference);
    }
    /**
     * Get current active locale
     *
     * @returns Current locale code
     *
     * @example
     * ```typescript
     * console.log(service.getCurrentLocale()); // 'ja'
     * ```
     */
    getCurrentLocale() {
        return this.currentLocale;
    }
    /**
     * Static method: Get current locale from default instance
     */
    static getCurrentLocale() {
        return this.getDefault().getCurrentLocale();
    }
    /**
     * Check if service is initialized with a locale
     *
     * @returns true if locale data is loaded
     *
     * @example
     * ```typescript
     * if (!service.isLocaleLoaded()) {
     *     await service.setLocale('en');
     * }
     * ```
     */
    isLocaleLoaded(locale) {
        // BUG-006: with an explicit locale, report on that locale's data instead
        // of the shared initialization flag
        if (locale !== undefined) {
            return this.registry.hasLocale(locale);
        }
        return this.isInitialized;
    }
    /**
     * Static method: Check if locale is loaded in default instance
     */
    static isLocaleLoaded(locale) {
        return this.getDefault().isLocaleLoaded(locale);
    }
    /**
     * Get localized UI label (with fallback to English)
     *
     * @param key - Translation key
     * @returns Localized label
     */
    getLabel(key, locale) {
        return this.translator.getLabel(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized label using default instance
     */
    static getLabel(key, locale) {
        return this.getDefault().getLabel(key, locale);
    }
    /**
     * Get localized dye name by itemID
     *
     * @param itemID - Dye item ID (5729-48227)
     * @returns Localized name or null if not found
     */
    getDyeName(itemID, locale) {
        return this.translator.getDyeName(itemID, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized dye name using default instance
     */
    static getDyeName(itemID, locale) {
        return this.getDefault().getDyeName(itemID, locale);
    }
    /**
     * Get localized category name
     *
     * @param category - Category key (e.g., "Reds", "Blues")
     * @returns Localized category name
     */
    getCategory(category, locale) {
        return this.translator.getCategory(category, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized category using default instance
     */
    static getCategory(category, locale) {
        return this.getDefault().getCategory(category, locale);
    }
    /**
     * Get localized acquisition method
     *
     * @param acquisition - Acquisition key (e.g., "Dye Vendor", "Crafting")
     * @returns Localized acquisition method
     */
    getAcquisition(acquisition, locale) {
        return this.translator.getAcquisition(acquisition, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized acquisition using default instance
     */
    static getAcquisition(acquisition, locale) {
        return this.getDefault().getAcquisition(acquisition, locale);
    }
    /**
     * Get localized currency display label
     *
     * @param currency - Currency key (e.g., "Gil", "Cosmocredits")
     * @returns Localized currency label
     */
    getCurrency(currency, locale) {
        return this.translator.getCurrency(currency, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized currency using default instance
     */
    static getCurrency(currency, locale) {
        return this.getDefault().getCurrency(currency, locale);
    }
    /**
     * Get localized harmony type name
     *
     * @param key - Harmony type key
     * @returns Localized harmony type
     */
    getHarmonyType(key, locale) {
        return this.translator.getHarmonyType(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized harmony type using default instance
     */
    static getHarmonyType(key, locale) {
        return this.getDefault().getHarmonyType(key, locale);
    }
    /** Localised colour-wheel name (Harmony Explorer wheel selector). */
    getColorWheelName(id, locale) {
        return this.translator.getColorWheelName(id, locale ?? this.currentLocale);
    }
    /** Static: see the instance method. */
    static getColorWheelName(id, locale) {
        return this.getDefault().getColorWheelName(id, locale);
    }
    /**
     * Get localized vision type name
     *
     * @param key - Vision type key
     * @returns Localized vision type
     */
    getVisionType(key, locale) {
        return this.translator.getVisionType(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized vision type using default instance
     */
    static getVisionType(key, locale) {
        return this.getDefault().getVisionType(key, locale);
    }
    /**
     * Get localized short vision-name (compact form, no parenthetical)
     *
     * @param key - Vision type key
     * @returns Localized short vision name
     */
    getVisionShort(key, locale) {
        return this.translator.getVisionShort(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized short vision-name using default instance
     */
    static getVisionShort(key, locale) {
        return this.getDefault().getVisionShort(key, locale);
    }
    /**
     * Get localized tool display name
     *
     * @param key - Tool key
     * @returns Localized tool display name
     * @deprecated I18N-003 — covers only the six pre-5.0 tools (the product ships
     * nine) and has no consumer in this monorepo: web-app names tools from its own
     * `tools.*.title` keys, og-worker from `OG_DECK`. Kept as published API; do not
     * build on it. Removal is a core major.
     */
    getToolName(key, locale) {
        return this.translator.getToolName(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized tool display name using default instance
     * @deprecated I18N-003 — see the instance method.
     */
    static getToolName(key, locale) {
        return this.getDefault().getToolName(key, locale);
    }
    /**
     * Get a localized Facewear tint name by its slug (I18N-008).
     *
     * The 11 Facewear colours are not dyes and are keyed by slug, so they do not
     * go through `getDyeName()`.
     */
    getFacewearColorName(id, locale) {
        return this.translator.getFacewearColorName(id, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get a localized Facewear tint name using default instance
     */
    static getFacewearColorName(id, locale) {
        return this.getDefault().getFacewearColorName(id, locale);
    }
    /**
     * Get localized color-sheet category name
     *
     * @param key - Sheet key
     * @returns Localized sheet name
     */
    getSheetName(key, locale) {
        return this.translator.getSheetName(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized sheet name using default instance
     */
    static getSheetName(key, locale) {
        return this.getDefault().getSheetName(key, locale);
    }
    /**
     * Get localized race name
     *
     * @param key - Race key
     * @returns Localized race name
     */
    getRace(key, locale) {
        return this.translator.getRace(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized race name using default instance
     */
    static getRace(key, locale) {
        return this.getDefault().getRace(key, locale);
    }
    /**
     * Get localized clan (subrace) name
     *
     * @param key - Clan key
     * @returns Localized clan name
     */
    getClan(key, locale) {
        return this.translator.getClan(key, locale ?? this.currentLocale);
    }
    /**
     * Static method: Get localized clan name using default instance
     */
    static getClan(key, locale) {
        return this.getDefault().getClan(key, locale);
    }
    /**
     * Get all available locale codes
     *
     * @returns Array of supported locale codes
     *
     * @public published `@xivdyetools/core` API — removal is a MAJOR (DEAD-006).
     */
    getAvailableLocales() {
        return [...SUPPORTED_LOCALES];
    }
    /**
     * Static method: Get available locales
     */
    static getAvailableLocales() {
        return [...SUPPORTED_LOCALES];
    }
    /**
     * Preload multiple locales for instant switching
     * Useful for apps that need to switch locales without delay
     *
     * BUG-058: this used `setLocale`, which also makes the locale ACTIVE — so
     * preloading `['ja','de','fr']` silently left the singleton speaking French,
     * and preloading in a different order gave a different active locale.
     * Loading is not switching; `ensureLocaleLoaded` registers the data without
     * touching the current locale, which is what "preload" means.
     *
     * @param locales - Array of locale codes to preload
     */
    async preloadLocales(locales) {
        await Promise.all(locales.map((locale) => this.ensureLocaleLoaded(locale)));
    }
    /**
     * Static method: Preload locales using default instance
     */
    static async preloadLocales(locales) {
        return this.getDefault().preloadLocales(locales);
    }
    /**
     * Clear all loaded locales from cache
     * Useful for testing or memory management
     */
    clear() {
        this.registry.clear();
        this.currentLocale = 'en';
        this.isInitialized = false;
    }
    /**
     * Static method: Clear cache of default instance
     */
    static clear() {
        this.getDefault().clear();
    }
    /**
     * Reset the static singleton instance
     * Useful for testing to prevent test pollution between test suites
     * Per Issue #6: Creates a fresh instance since we use eager initialization
     *
     * @example
     * ```typescript
     * // In test cleanup
     * afterEach(() => {
     *     LocalizationService.resetInstance();
     * });
     * ```
     */
    static resetInstance() {
        this.defaultInstance.clear();
        this.defaultInstance = new LocalizationService();
    }
}
//# sourceMappingURL=LocalizationService.js.map